export type DiscountRuleBase = {
  id: number;
  order: number;
};

export type DiscountTreePathPart = {
  kind: 'vendor' | 'outlet' | 'service-category' | 'product';
  id: number;
  label: string;
};

type DiscountRuleWithPath = DiscountRuleBase & {
  target_path: readonly DiscountTreePathPart[];
};

export type DiscountTreeNode<Rule extends DiscountRuleBase, Context = unknown> = {
  id: string;
  label: string;
  kind: DiscountTreePathPart['kind'];
  rules: readonly Rule[];
  children: readonly DiscountTreeNode<Rule, Context>[];
} & Context;

type MutableDiscountTreeNode<Rule extends DiscountRuleBase> = Omit<DiscountTreeNode<Rule>, 'children' | 'rules'> & {
  children: MutableDiscountTreeNode<Rule>[];
  rules: Rule[];
};

/**
 * Build the sparse tree represented by authored rules only.
 *
 * Discovery data is intentionally not an input: saved targets stay visible when
 * they disappear from a current catalogue or provided-scope response.
 */
export function buildSparseTree<Rule extends DiscountRuleWithPath>(rules: readonly Rule[]): DiscountTreeNode<Rule>[] {
  const roots: MutableDiscountTreeNode<Rule>[] = [];

  for (const rule of rules) {
    const path = rule.target_path;
    let siblings = roots;
    path.forEach((part, index) => {
      const id = `${part.kind}-${part.id}`;
      let node = siblings.find((candidate) => candidate.id === id);
      if (!node) {
        node = { id, label: part.label, kind: part.kind, rules: [], children: [] };
        siblings.push(node);
      }
      if (index === path.length - 1) {
        node.rules.push(rule);
      }
      siblings = node.children;
    });
  }

  const normalize = (nodes: readonly MutableDiscountTreeNode<Rule>[]): DiscountTreeNode<Rule>[] =>
    nodes.map((node) => ({
      ...node,
      rules: [...node.rules],
      children: normalize(node.children),
    }));

  return normalize(roots);
}
